
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

interface StatisticCardProps {
  title: string;
  value: string;
  change: string;
  positive?: boolean;
  icon: LucideIcon;
  color?: 'neon-blue' | 'neon-purple' | 'neon-pink' | 'neon-green' | 'neon-yellow' | 'neon-cyan' | 'neon-red';
  className?: string;
}

const StatisticCard: React.FC<StatisticCardProps> = ({
  title,
  value,
  change,
  positive = true,
  icon: Icon,
  color = 'neon-blue',
  className
}) => {
  // Get color classes
  const getColorClasses = () => {
    switch (color) {
      case 'neon-blue':
        return 'border-neon-blue/20 hover:border-neon-blue/40 group-hover:shadow-neon-blue/30';
      case 'neon-purple':
        return 'border-neon-purple/20 hover:border-neon-purple/40 group-hover:shadow-neon-purple/30';
      case 'neon-pink':
        return 'border-neon-pink/20 hover:border-neon-pink/40 group-hover:shadow-neon-pink/30';
      case 'neon-green':
        return 'border-neon-green/20 hover:border-neon-green/40 group-hover:shadow-neon-green/30';
      case 'neon-yellow':
        return 'border-neon-yellow/20 hover:border-neon-yellow/40 group-hover:shadow-neon-yellow/30';
      case 'neon-cyan':
        return 'border-neon-cyan/20 hover:border-neon-cyan/40 group-hover:shadow-neon-cyan/30';
      case 'neon-red':
        return 'border-neon-red/20 hover:border-neon-red/40 group-hover:shadow-neon-red/30';
      default:
        return 'border-neon-blue/20 hover:border-neon-blue/40 group-hover:shadow-neon-blue/30';
    }
  };

  // Get text color
  const getTextColor = () => {
    switch (color) {
      case 'neon-blue': return 'text-neon-blue';
      case 'neon-purple': return 'text-neon-purple';
      case 'neon-pink': return 'text-neon-pink';
      case 'neon-green': return 'text-neon-green';
      case 'neon-yellow': return 'text-neon-yellow';
      case 'neon-cyan': return 'text-neon-cyan';
      case 'neon-red': return 'text-neon-red';
      default: return 'text-neon-blue';
    }
  };

  return (
    <Card 
      className={cn(
        "backdrop-blur-md transition-all group hover:-translate-y-1 cursor-pointer bg-black/50 border",
        getColorClasses(),
        className
      )}
      variant="neo"
    >
      {/* Subtle inner glow effect */}
      <div className={cn(
        "absolute inset-0 opacity-20 rounded-xl bg-gradient-to-br from-current via-transparent to-transparent",
        getTextColor()
      )} />
      
      {/* Animated border effect */}
      <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
        <div className="absolute top-0 -left-full w-[200%] h-[1px] bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[slide_2s_linear_infinite]"></div>
      </div>
      
      <CardContent className="p-4 relative">
        <div className="flex justify-between items-start">
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-white/90">{title}</h3>
            <p className="text-2xl font-bold text-white group-hover:text-white transition-colors">{value}</p>
            <div className={cn(
              "text-xs font-medium flex items-center gap-1",
              positive ? "text-green-400" : "text-rose-400"
            )}>
              {positive ? '↑' : '↓'} {change}
            </div>
          </div>
          <div className={cn(
            "h-12 w-12 rounded-full flex items-center justify-center backdrop-blur-sm relative group-hover:scale-110 transition-transform duration-500 border",
            `bg-black/30 border-${color}/30`
          )}>
            <Icon className={cn("h-6 w-6", getTextColor())} />
            
            {/* Glow effect on hover */}
            <div className={cn(
              "absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm -z-10",
              `bg-${color}/20`
            )} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StatisticCard;
